import React, { useEffect, useState } from "react";
import { Form, Table, Alert } from "react-bootstrap";
import {
  organizerListScannersApi,
  organizerCreateScannerApi,
  organizerDeleteScannerApi,
  organizerUnassignScannerApi,
  organizerListEventsApi,
  organizerAssignScannerWithTypesApi,
  organizerTicketTypesByEventApi,
} from "../../api/api";
import { handleError } from "../../helpers/handleError";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import OrganizerPillSelect from "../components/OrganizerPillSelect";
import "../dashboard.css";

const Scanners = () => {
  const [list, setList] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [assign, setAssign] = useState({ eventId: "", scannerId: "" });
  const [ticketTypes, setTicketTypes] = useState([]);
  const [selectedTypeIds, setSelectedTypeIds] = useState([]);
  const [allowAllTypes, setAllowAllTypes] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [sc, ev] = await Promise.all([organizerListScannersApi(), organizerListEventsApi()]);
      setList(Array.isArray(sc?.data?.info) ? sc.data.info : []);
      setEvents(Array.isArray(ev?.data?.info) ? ev.data.info : []);
    } catch (e) {
      handleError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        setTicketTypes([]);
        setSelectedTypeIds([]);
        if (!assign.eventId) return;
        const res = await organizerTicketTypesByEventApi({ eventid: assign.eventId });
        const list = Array.isArray(res?.data?.info) ? res.data.info : [];
        setTicketTypes(list);
      } catch (e) {
        console.error(e);
      }
    };
    fetchTypes();
  }, [assign.eventId]);

  const onCreate = async (e) => {
    e.preventDefault();
    setError("");
    try {
      setLoading(true);
      await organizerCreateScannerApi(form);
      setForm({ name: "", email: "", password: "" });
      await load();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || "Error al crear escáner");
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar escáner?")) return;
    try {
      setLoading(true);
      await organizerDeleteScannerApi(id);
      await load();
    } catch (e) {
      handleError(e);
    } finally {
      setLoading(false);
    }
  };

  const onAssign = async () => {
    if (!assign.eventId || !assign.scannerId) return;
    if (!allowAllTypes && selectedTypeIds.length === 0) {
      alert('Selecciona al menos un tipo de entrada o activa "Permitir todas".');
      return;
    }
    try {
      setLoading(true);
      if (allowAllTypes) {
        await organizerAssignScannerWithTypesApi({ ...assign, allowAll: true });
      } else {
        await organizerAssignScannerWithTypesApi({ ...assign, ticketTypeIds: selectedTypeIds });
      }
      await load();
      alert("Asignado");
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const onUnassign = async () => {
    if (!assign.eventId || !assign.scannerId) return;
    try {
      setLoading(true);
      await organizerUnassignScannerApi(assign);
      await load();
      alert("Revocado");
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <SpinLoading />;

  return (
    <div className="organizer-page">
      <section className="organizer-section">
      <div className="organizer-section__header">
        <div>
          <p className="organizer-eyebrow">Accesos</p>
          <h2>Escáneres</h2>
          <p className="organizer-section__subtitle">Crea cuentas y controlá qué evento puede validar cada usuario.</p>
        </div>
      </div>
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      <div className="organizer-form-grid organizer-form-grid--two">
        <div className="organizer-form-card">
          <h3>Crear nuevo escáner</h3>
          <Form onSubmit={onCreate}>
            <Form.Control
              className="organizer-field"
              placeholder="Nombre"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <Form.Control
              className="organizer-field"
              placeholder="Correo electrónico"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
            <Form.Control
              className="organizer-field"
              placeholder="Contraseña"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
            />
            <div className="organizer-form-actions">
              <button type="submit" className="organizer-btn">Crear</button>
            </div>
          </Form>
        </div>
        <div className="organizer-form-card">
          <h3>Asignar escáner a evento</h3>
          <OrganizerPillSelect
            placeholder="Selecciona evento"
            value={assign.eventId}
            onChange={(val) => setAssign({ ...assign, eventId: val ? String(val) : "" })}
            options={events
              .map((ev) => {
                const raw = ev?.id ?? ev?._id ?? ev?.eventid;
                if (!raw) return null;
                return { value: String(raw), label: ev?.name || "Evento sin nombre" };
              })
              .filter(Boolean)}
          />
          {!!assign.eventId && (
            <div>
              <Form.Check
                type="switch"
                id="scanner-allow-all"
                label="Permitir todas las entradas"
                checked={allowAllTypes}
                onChange={(e) => setAllowAllTypes(e.target.checked)}
                className="mb-2"
              />
              {!allowAllTypes && (
                <div className="organizer-scroll-panel mb-3">
                  {ticketTypes.length === 0 && <div className="text-muted">No hay tipos de entradas para este evento</div>}
                  {ticketTypes.map((tt) => (
                    <Form.Check
                      key={tt._id || tt.id}
                      type="checkbox"
                      id={`scanner-tt-${tt._id || tt.id}`}
                      label={`${tt.name}${tt.zone || tt.gate ? ` · ${[tt.gate, tt.zone].filter(Boolean).join(" · ")}` : ""}`}
                      checked={selectedTypeIds.includes(String(tt._id || tt.id))}
                      onChange={(e) => {
                        const id = String(tt._id || tt.id);
                        setSelectedTypeIds((prev) =>
                          e.target.checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)
                        );
                      }}
                      className="mb-1"
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          <OrganizerPillSelect
            placeholder="Selecciona escáner"
            value={assign.scannerId}
            onChange={(val) => setAssign({ ...assign, scannerId: val ? String(val) : "" })}
            options={list.map((u) => ({
              value: String(u._id),
              label: `${u.name} (${u.email})`,
            }))}
          />
          <div className="organizer-form-actions">
            <button type="button" className="organizer-btn" onClick={onAssign}>
              Asignar
            </button>
            <button type="button" className="organizer-btn secondary" onClick={onUnassign}>
              Revocar
            </button>
          </div>
        </div>
      </div>

      <div className="organizer-card">
        <div className="organizer-card__header">
          <div>
            <h3 className="organizer-card__title">Mis escáneres</h3>
            <p className="organizer-card__subtitle">Controlá quién puede validar entradas y revocá accesos.</p>
          </div>
          <div className="organizer-chip organizer-chip--secondary">{list.length} escáneres</div>
        </div>
        <div className="organizer-table">
          <Table responsive className="organizer-table__inner align-middle">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Correo</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {list.map((u) => (
                <tr key={u._id}>
                  <td>{u.name}</td>
                  <td>{u.email}</td>
                  <td>
                    <button type="button" className="organizer-link-btn danger" onClick={() => onDelete(u._id)}>
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-center text-muted py-3">
                    Sin escáneres
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
      </section>
    </div>
  );
};

export default Scanners;
